import { type FC } from "react";

import { cn } from "@/components/podkit/lib/cn";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { IconPlusLarge } from "central-icons/IconPlusLarge";
import { Tooltip } from "@/components/Tooltip";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";

import { CreateEnvironmentModalKeyBinding } from "@/utils/platform";
import { Button } from "@/components/flexkit/Button";

type CreateSessionButtonProps = {
    isLocked: boolean;
    onClick?: () => void;
};

export const NewEnvironmentButton: FC<CreateSessionButtonProps> = ({ isLocked, onClick }) => {
    const button = (
        <Button
            variant="outline"
            data-testid="create-session-button"
            aria-label="New Environment"
            onClick={onClick}
            className={cn("relative h-9 gap-0 px-0 py-0", {
                "w-9": !isLocked,
                "w-full": isLocked,
            })}
            data-tracking-id="new-environment"
        >
            <CreateSessionIcon />
            <CreateSessionText isLocked={isLocked} />
        </Button>
    );

    if (!isLocked) {
        return (
            <Tooltip content="New Environment" side="right" usePortal>
                {button}
            </Tooltip>
        );
    }

    return button;
};

type CreateSessionComponentProps = {
    isLocked: boolean;
};

const CreateSessionText: FC<CreateSessionComponentProps> = ({ isLocked }) => {
    const { isMobileViewport } = useIsMobileViewport();

    return (
        <div
            className={cn(
                "absolute left-9 right-0 flex items-center justify-between overflow-hidden pr-2",
                isLocked ? "opacity-100" : "opacity-0",
            )}
        >
            <span data-testid="session-text" className="whitespace-nowrap">
                New Environment
            </span>
            {!isMobileViewport && (
                <div data-testid="keyboard-shortcut">
                    <KeyboardCombo
                        keys={CreateEnvironmentModalKeyBinding}
                        className="bg-surface-muted text-content-muted"
                    />
                </div>
            )}
        </div>
    );
};

const CreateSessionIcon: FC = (): JSX.Element => {
    return (
        <div
            data-testid="create-session-icon"
            className="absolute -left-px flex h-9 w-9 shrink-0 items-center justify-center"
        >
            <IconPlusLarge size={16} />
        </div>
    );
};
